import { Link } from "react-router-dom";
import { Instagram, Mail, Phone } from "lucide-react";
import { BUSINESS, FOOTER_LINKS } from "@/data/site";
import { DiamondMark } from "@/components/Nav";
import { useSiteData } from "@/context/SiteData";

export default function Footer() {
  const year = new Date().getFullYear();
  const { t } = useSiteData();
  return (
    <footer className="bg-[#171310] text-[#B7AA99]" data-testid="site-footer">
      <div className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 md:grid-cols-12 lg:px-8">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3">
            <img
              src="/assets/logo.png"
              alt="Diamond's Divine Arts logo"
              className="h-11 w-11 rounded-full object-cover ring-1 ring-[#D4AF37]/50"
            />
            <span className="font-display text-sm font-semibold tracking-[0.2em] text-[#FAF7F2]">
              DIAMOND'S DIVINE ARTS
            </span>
          </div>
          <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.35em] text-[#B89759]">
            {BUSINESS.tagline}
          </p>
          <p className="mt-5 max-w-sm text-sm font-light leading-relaxed">
            {t("footer_tagline", "An artistic ministry creating faith-inspired artwork — visual illustrations of the Word, painted to strengthen faith and bring scripture to life through creativity.")}
          </p>
        </div>

        <div className="md:col-span-3">
          <h3 className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#B89759]">Navigate</h3>
          <ul className="mt-5 space-y-3">
            {FOOTER_LINKS.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  className="text-sm transition-colors hover:text-[#D4AF37]"
                  data-testid={`footer-link-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </Link>
              </li>
            ))}
            <li>
              <Link to="/policies" className="text-sm transition-colors hover:text-[#D4AF37]" data-testid="footer-link-policies">
                Policies
              </Link>
            </li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <h3 className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#B89759]">Connect</h3>
          <ul className="mt-5 space-y-3 text-sm">
            <li>
              <a
                href={`mailto:${BUSINESS.email}`}
                className="inline-flex items-center gap-3 transition-colors hover:text-[#D4AF37]"
                data-testid="footer-email"
              >
                <Mail className="h-4 w-4 text-[#B89759]" /> {BUSINESS.email}
              </a>
            </li>
            <li>
              <a
                href={BUSINESS.phoneHref}
                className="inline-flex items-center gap-3 transition-colors hover:text-[#D4AF37]"
                data-testid="footer-phone"
              >
                <Phone className="h-4 w-4 text-[#B89759]" /> {BUSINESS.phone}
              </a>
            </li>
            <li>
              <a
                href={BUSINESS.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 transition-colors hover:text-[#D4AF37]"
                data-testid="footer-instagram"
              >
                <Instagram className="h-4 w-4 text-[#B89759]" /> {BUSINESS.instagram}
              </a>
            </li>
          </ul>
          <a
            href={BUSINESS.instagramUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost-light mt-6 !px-6 !py-2.5 !text-xs"
            data-testid="footer-follow-button"
          >
            <Instagram className="h-4 w-4" /> Follow on Instagram
          </a>
        </div>
      </div>

      <div className="border-t border-[#FAF7F2]/10">
        <div className="mx-auto flex max-w-7xl flex-col justify-between gap-3 px-4 py-6 text-xs sm:px-6 md:flex-row lg:px-8">
          <span data-testid="footer-copyright">© {year} Diamond's Divine Arts. All rights reserved.</span>
          <span className="text-[#8C7E70]">
            Faith-inspired original artwork by Natalia.
          </span>
        </div>
      </div>

      <div className="border-t border-[#FAF7F2]/10">
        <div className="mx-auto max-w-7xl px-4 py-4 text-center sm:px-6 lg:px-8">
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#8C7E70]" data-testid="footer-credit">
            Created by{" "}
            <a
              href="https://omraowebstudio.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#B89759] transition-colors hover:text-[#D4AF37]"
              data-testid="footer-credit-link"
            >
              OMRAO Web Studio
            </a>
          </span>
        </div>
      </div>
    </footer>
  );
}
